import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin } from 'lucide-react';
import { Restaurant } from '../../lib/types';
import { cn } from '../../lib/utils';
interface RestaurantCardProps {
  restaurant: Restaurant;
  zoneName?: string;
  tags?: string[];
}
export function RestaurantCard({
  restaurant,
  zoneName,
  tags = []
}: RestaurantCardProps) {
  const accentColor = restaurant.primary_color || '#C9A34E';
  return <Link to={`/r/${restaurant.slug}`} className="block group" data-id="element-265">
      <div className="relative h-[280px] rounded-2xl overflow-hidden border border-white/5 bg-[#141414] transition-shadow duration-300 hover:shadow-[0_0_20px_rgba(201,163,78,0.12)]" data-id="element-266">
        {/* Cover Image */}
        {restaurant.cover_url ? <img src={restaurant.cover_url} alt={restaurant.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" decoding="async" data-id="element-267" /> : <div className="absolute inset-0 w-full h-full" style={{
        background: `linear-gradient(135deg, ${accentColor}33 0%, #0B0B0B 100%)`
      }} data-id="element-268" />}

        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" data-id="element-269" />

        {/* Top badges */}
        <div className="absolute top-3 left-3 right-3 flex items-start justify-between z-10" data-id="element-270">
          {/* Featured badge */}
          {restaurant.is_featured && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#C9A34E] text-[#0B0B0B]" data-id="element-271">
              ★ Destacado
            </span>}
          {!restaurant.is_featured && <span data-id="element-272" />}

          {/* Open/Closed indicator */}
          {!restaurant.is_open ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-500/90 text-white" data-id="element-273">
              Cerrado
            </span> : <span className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/50" data-id="element-274">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" data-id="element-275" />
              <span className="text-[10px] font-medium text-white/80" data-id="element-276">
                Abierto
              </span>
            </span>}
        </div>

        {/* Bottom content */}
        <div className="absolute bottom-0 left-0 right-0 p-4 z-10" data-id="element-277">
          <div className="flex items-end gap-3" data-id="element-278">
            {/* Logo */}
            <div className="w-11 h-11 rounded-xl overflow-hidden border-2 border-white/20 shrink-0 bg-[#0B0B0B]" data-id="element-279">
              {restaurant.logo_url ? <img src={restaurant.logo_url} alt={`${restaurant.name} logo`} className="w-full h-full object-cover" loading="lazy" data-id="element-280" /> : <div className="w-full h-full flex items-center justify-center text-white font-bold text-base" style={{
              backgroundColor: accentColor
            }} data-id="element-281">
                  {restaurant.name.charAt(0)}
                </div>}
            </div>

            {/* Text content */}
            <div className="flex-1 min-w-0" data-id="element-282">
              <h3 className="font-bold text-white text-base leading-tight line-clamp-1 mb-0.5" data-id="element-283">
                {restaurant.name}
              </h3>
              <p className="text-white/50 text-xs line-clamp-1" data-id="element-284">
                {restaurant.description_short || 'Deliciosa comida preparada con amor.'}
              </p>
            </div>
          </div>

          {/* Meta row */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10" data-id="element-285">
            <div className="flex items-center gap-3" data-id="element-286">
              <div className="flex items-center gap-1 text-white/50" data-id="element-287">
                <MapPin className="w-3 h-3" data-id="element-288" />
                <span className="text-[11px] font-medium" data-id="element-289">
                  {zoneName || restaurant.address_text || 'Los Santos'}
                </span>
              </div>
              <div className="flex items-center gap-1" data-id="element-290">
                <Star className="w-3 h-3 fill-[#C9A34E] text-[#C9A34E]" data-id="element-291" />
                <span className="text-[11px] font-semibold text-[#C9A34E]" data-id="element-292">
                  4.8
                </span>
              </div>
            </div>

            <span className="text-xs font-bold transition-colors" style={{
            color: accentColor
          }} data-id="element-293">
              {restaurant.is_open ? 'Ordenar →' : 'Ver menú →'}
            </span>
          </div>
        </div>
      </div>
    </Link>;
}